import type {
  WritingAssistRequest,
  WritingAssistResponse,
  WritingAssistSuccessResponse,
} from 'api/Api';

export type {
  WritingAssistContext,
  WritingAssistRequest,
  WritingAssistResponse,
  WritingAssistSuccessResponse,
  WritingAssistUsage,
  WritingAssistViolationCategory,
} from 'api/Api';

export type WritingAssistVersion = 'original' | 'tidied' | 'enhanced';
export type WritingAssistVariant = 'profile' | 'signup';

export interface WritingAssistSaveSelection {
  text: string;
  version: WritingAssistVersion;
  hash?: string;
  saveAsBase?: boolean;
}

interface WritingAssistCommonProps {
  initialText?: string;
  className?: string;
  requestWritingAssist?: (request: WritingAssistRequest) => Promise<WritingAssistResponse>;
  onTextChange?: (text: string) => void;
  onSave: (selection: WritingAssistSaveSelection) => unknown | Promise<unknown>;
  onSkip?: () => void;
  /** T2-6891 may consume the untouched writer envelope (including coach/refusal). */
  onWriterResult?: (result: WritingAssistResponse) => void;
  /** T2-6891 may consume the untouched result returned by the context save boundary. */
  onGateResult?: (result: unknown) => void;
  onSaveError?: (error: unknown) => void;
  /** Increment to open compose on this text and immediately request a polish. */
  autoPolishRequest?: number;
}

type WritingAssistContextProps =
  | {
    context: 'aboutMe';
    firstName: string;
    variant?: WritingAssistVariant;
    entityId?: never;
    practiceName?: never;
  }
  | {
    context: 'baseCoverLetter';
    firstName?: never;
    variant?: never;
    entityId?: never;
    practiceName?: never;
  }
  | {
    context: 'applicationCoverLetter';
    practiceName: string;
    entityId: number;
    firstName?: never;
    variant?: never;
  }
  | {
    context: 'jobDescription';
    entityId: number;
    firstName?: never;
    practiceName?: never;
    variant?: never;
  };

export type WritingAssistProps = WritingAssistCommonProps & WritingAssistContextProps;

export function isWritingAssistSuccess(
  response: WritingAssistResponse,
): response is WritingAssistSuccessResponse {
  const candidate = response as WritingAssistSuccessResponse;
  return Boolean(
    candidate
      && candidate.policy
      && candidate.policy.blocking === false
      && Array.isArray(candidate.policy.violations)
      && typeof candidate.tidied === 'string'
      && candidate.tidied.length > 0
      && typeof candidate.enhanced === 'string'
      && candidate.enhanced.length > 0
      && candidate.coach
      && Array.isArray(candidate.coach.observations)
      && candidate.hashes
      && typeof candidate.hashes.tidied === 'string'
      && typeof candidate.hashes.enhanced === 'string'
      && candidate.assists
      && Number.isInteger(candidate.assists.used)
      && candidate.assists.used >= 0
      && Number.isInteger(candidate.assists.max)
      && candidate.assists.max > 0,
  );
}
